/** Keep native scroll/focus targets below this drawer's own sticky header.
 * Header and scoped content change with viewport, audit and contact reads.
 * No document/global styles or scroll positions are changed here. */
export function observeDrawerScrollInset(panel, header, Observer = globalThis.ResizeObserver) {
  if (!panel || !header) return () => {}
  const previous = panel.style.scrollPaddingTop
  let previousHeight = null, previousWidth = null, previousExtent = null, previousOffset = null, disposed = false, anchor = null, expectedScrollTop = panel.scrollTop
  const focusedTask = () => {
    const target = panel.ownerDocument?.activeElement
    return target?.matches('[data-work-section]') && panel.contains(target) ? target : null
  }
  // Content position is invariant under scrolling, unlike viewport top. This
  // separates native layout anchoring from an unrelated user/programmatic scroll.
  const contentOffset = target => target ? target.getBoundingClientRect().top + (panel.scrollTop || 0) - (panel.getBoundingClientRect?.().top || 0) : null
  const extent = () => panel.scrollHeight ?? null
  const contentChanged = target => extent() !== previousExtent || (target && previousOffset != null && Math.abs(contentOffset(target) - previousOffset) > 0.5)
  const rememberContent = target => { previousExtent = extent(); previousOffset = contentOffset(target) }
  const release = () => { anchor = null }
  const focus = () => {
    if (disposed) return
    const target = focusedTask()
    anchor = target
    if (!target) return
    // The app focuses and scrolls the task in the same call. Remember that
    // native scroll, not a later user scroll; never schedule a refocus/scroll.
    queueMicrotask(() => { if (!disposed && anchor === target) { expectedScrollTop = panel.scrollTop; rememberContent(target) } })
  }
  const scroll = () => {
    if (disposed || !anchor) return
    // Native scroll anchoring can emit scroll before ResizeObserver. A layout
    // change is handled by measure; an unrelated scroll relinquishes the task.
    if (header.getBoundingClientRect().height !== previousHeight || panel.clientWidth !== previousWidth || contentChanged(anchor)) return
    if (panel.scrollTop !== expectedScrollTop) release()
  }
  const key = event => { if (!disposed && ['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' ','Spacebar'].includes(event.key)) release() }
  const measure = () => {
    if (disposed) return
    const { height, bottom } = header.getBoundingClientRect()
    if (!Number.isFinite(height) || height < 0) return
    panel.style.scrollPaddingTop = `${Math.ceil(height) + 8}px`
    const growth = previousHeight == null ? 0 : height - previousHeight
    const target = focusedTask()
    const changed = previousHeight != null && (height !== previousHeight || panel.clientWidth !== previousWidth || contentChanged(target))
    previousHeight = height
    previousWidth = panel.clientWidth
    // Header shrink can also cover task focus: content ABOVE the task reflows
    // by a different amount on widening. Only the untouched app-focus anchor
    // is retained; explicit wheel/touch/keyboard/scrollbar intent releases it.
    if (changed && anchor && target === anchor) {
      const rect = target.getBoundingClientRect()
      if (rect.top < bottom && rect.bottom > bottom) panel.scrollTop -= Math.max(growth, bottom + 8 - rect.top)
    }
    if (changed) expectedScrollTop = panel.scrollTop
    rememberContent(target)
  }
  measure()
  const initial = focusedTask()
  if (initial && initial.getBoundingClientRect().top >= header.getBoundingClientRect().bottom) anchor = initial
  const listeners = [['focusin',focus],['focusout',release],['scroll',scroll],['wheel',release],['touchmove',release],['pointerdown',release],['keydown',key]]
  for (const [name,listener] of listeners) panel.addEventListener?.(name,listener,{capture:true,passive:true})
  const observer = typeof Observer === 'function' ? new Observer(measure) : null
  observer?.observe(header)
  observer?.observe(panel)
  // Async contacts and other drawer sections can change task position without
  // resizing the fixed panel or sticky header. Observe only this drawer's
  // existing structural content, not the document or an unbounded mutation tree.
  for (const child of panel.children || []) {
    if (child !== header) observer?.observe(child)
    for (const section of child.children || []) observer?.observe(section)
  }
  const dispose = () => {
    disposed = true; release(); observer?.disconnect()
    for (const [name,listener] of listeners) panel.removeEventListener?.(name,listener,true)
    panel.style.scrollPaddingTop = previous
  }
  // React layout commits and app-directed focus need the same scoped sampling
  // before ResizeObserver delivery. This does not reacquire a released anchor.
  dispose.sync = measure
  return dispose
}
